import {
  WebGLRenderer,
  OrthographicCamera,
  Scene,
  LinearFilter,
  RGBAFormat,
  WebGLRenderTarget,
  PlaneGeometry,
  Mesh,
  RawShaderMaterial,
  type Texture,
  Vector2,
  type Vector3,
  DataTexture,
  type Color,
  type Vector4,
  type Quaternion,
  type Matrix3,
  type Matrix4,
  type CubeTexture,
  GLSL3,
} from "three";

import DEFAULT_VERTEX_SHADER from "../shaders/singlepassnode/default.vs.glsl";
import DEFAULT_FRAGMENT_SHADER from "../shaders/singlepassnode/default.fs.glsl";

let adhocRenderer: WebGLRenderer | null = null;

export type ThreeUniformType =
  | number
  | boolean
  | Vector2
  | Vector3
  | Float32Array
  | Array<number>
  | Color
  | Vector4
  | Quaternion
  | Matrix3
  | Matrix4
  | Int32Array
  | Texture
  | CubeTexture;

/**
 * Singleton of a WebGlRenderer
 * @returns
 */
export function getAdhocRenderer(): WebGLRenderer {
  if (!adhocRenderer) {
    adhocRenderer = new WebGLRenderer({
      alpha: true,
      premultipliedAlpha: false,
    });
  }
  return adhocRenderer;
}

/**
 * An instance of SinglePassNode is used to produce a rendering. This could take uniforms such as texture or parameters
 * and create output of parametric sizes.
 * A good example of usage is to use two SinglePassNode to create a gaussian blur as the output of a SinglePassNode can be the resulting image as a texture
 * to be sent to a second pass.
 */
export default class SinglePassNode {
  private renderer: WebGLRenderer; //= new WebGLRenderer();
  private camera: OrthographicCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private scene: Scene = new Scene();
  private renderTarget: WebGLRenderTarget;
  private material: RawShaderMaterial = new RawShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: DEFAULT_VERTEX_SHADER,
    fragmentShader: DEFAULT_FRAGMENT_SHADER,
    depthWrite: false,
    transparent: true,
  });
  private size: Vector2;

  /**
   *
   * @param width width of the output
   * @param height height of the output
   * @param renderer optional existing renderer to use
   */
  constructor(width: number, height: number, renderer: WebGLRenderer = getAdhocRenderer()) {
    this.size = new Vector2(width, height);
    this.renderTarget = new WebGLRenderTarget(width, height, {
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      format: RGBAFormat,
    });
    this.camera.matrixAutoUpdate = false;
    this.renderer = renderer;
    this.renderer.setPixelRatio(1);
    // this.renderer.setSize(width, height);
    this.renderer.autoClear = false;
    // this.renderer.setRenderTarget( this.renderTarget );
    const mesh = new Mesh(new PlaneGeometry(2, 2), this.material);
    this.scene.add(mesh);
  }

  /**
   * Get the output texture
   * @param forceDataTexture Makes a deep copy of the texture data to return a `THREE.DataTexture`. This is much slower
   * but allows this instance to be disposed while keeping a copy of the texture.
   * @returns
   */
  getOutputTexture(forceDataTexture = false): Texture | null {
    if (forceDataTexture) {
      return new DataTexture(this.getPixelData(), this.size.x, this.size.y, RGBAFormat);
    }
    return this.renderTarget.texture;
  }

  /**
   * Set the size of the output
   * @param width
   * @param height
   * @returns
   */
  setSize(width: number, height: number) {
    if (width === this.size.x && height === this.size.y) {
      return;
    }

    this.size.x = width;
    this.size.y = height;
    // this.renderer.setSize( width, height );
    // in case of changing size, we have to reinstanciate this:
    this.renderTarget = new WebGLRenderTarget(width, height, {
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      format: RGBAFormat,
    });
    this.renderer.setRenderTarget(this.renderTarget);
  }

  /**
   * Set the vertex shader for this pass
   * @param shader
   */
  setVertexShader(shader: string) {
    this.material.vertexShader = shader.trim();
    this.material.needsUpdate = true;
  }

  /**
   * Set the fragment shader for this pass
   * @param shader
   */
  setFragmentShader(shader: string) {
    this.material.fragmentShader = shader.trim();
    this.material.needsUpdate = true;
  }

  /**
   * Add a uniform
   * @param name
   * @param value
   */
  setUniform(name: string, value: ThreeUniformType) {
    this.material.uniforms[name] = { value };
  }

  /**
   * Dynamically replace some string in a shader (`name`) by others (`value`)
   * @param name
   * @param value
   */
  setDefine(name: string, value: string | number | boolean) {
    this.material.fragmentShader = this.material.fragmentShader.replace(new RegExp(name, "g"), value.toString());
    this.material.vertexShader = this.material.vertexShader.replace(new RegExp(name, "g"), value.toString());
    this.material.needsUpdate = true;
  }

  /**
   * Retrieve the pixel data as a single dimension array of RGBA.
   * This can be slow because of memory transfer from GPU to CPU
   */
  getPixelData(): Uint8Array {
    const size = this.size;
    const pixels = new Uint8Array(size.x * size.y * 4);
    this.renderer.readRenderTargetPixels(this.renderTarget, 0, 0, size.x, size.y, pixels);
    return pixels;
  }

  /**
   * Get the defined size of the output
   * @returns
   */
  getSize(): Vector2 {
    return this.size.clone();
  }

  /**
   * Run the shader code. The result is then stored internaly in a `THREE.RenderTarget`
   * @returns
   */
  process(): Texture {
    this.material.needsUpdate = true;
    this.renderer.setRenderTarget(this.renderTarget);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    return this.renderTarget.texture;
  }

  /**
   * Opens the output image in a separate window for debugging purposes.
   */
  debugAsPNG() {
    const size = this.getSize();
    const data = this.getPixelData();
    const can = document.createElement("canvas");
    can.width = size.x;
    can.height = size.y;

    const ctx = can.getContext("2d");

    if (!ctx) {
      return;
    }

    const ctxData = ctx.getImageData(0, 0, can.width, can.height);
    ctxData.data.set(data);
    ctx.putImageData(ctxData, 0, 0);
    const win = window.open();

    if (!win) {
      return;
    }

    win.document.write(
      `<iframe src="${can.toDataURL(
        "image/png",
      )}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`,
    );
  }

  /**
   * Free the render target memory
   */
  dispose() {
    this.renderTarget.texture.dispose();
    this.renderTarget.dispose();
  }
}
